import React from "react";
import { useLocation } from "react-router-dom";
import { SITE_URL, SITE_NAME, DEFAULT_DESCRIPTION, OG_IMAGE } from "@/data/site";

/**
 * Lightweight <SEO/> — updates document head on mount without any external library.
 * Handles title, description, canonical, Open Graph, Twitter Card, and JSON-LD schema.
 * Cleans up injected schema scripts on unmount so pages don't leak stale JSON-LD.
 */
function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  image = OG_IMAGE,
  type = "website",
  schema = [], // array of JSON-LD objects
  noindex = false,
}) {
  const location = useLocation();
  const effectivePath = path ?? location.pathname;
  const canonical = `${SITE_URL}${effectivePath === "/" ? "" : effectivePath}`;
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Real Estate Listing Support Handled as a System`;

  React.useEffect(() => {
    document.title = fullTitle;

    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    setLink("canonical", canonical);

    // Open Graph
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", image);
    setMeta("property", "og:site_name", SITE_NAME);

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);

    // Inject page-scoped JSON-LD
    const scriptEls = [];
    const list = Array.isArray(schema) ? schema : [schema];
    list.filter(Boolean).forEach((obj, i) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-scope", "page");
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      scriptEls.push(s);
    });

    return () => {
      scriptEls.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullTitle, description, canonical, image, type, noindex, JSON.stringify(schema)]);

  return null;
}
